// Canvas core. Legacy: initCanvas/resizeCanvas/zoom/grid (snapshot L3193-3292,
// 3878-3946, 5410-5477). Fabric v7 changes applied (map §4):
//   - named import `Canvas` (not `new fabric.Canvas`)
//   - prefer `requestRenderAll()`
//   - the canvas singleton lives in state.ts, not a file-scope global
import { Canvas, FabricText, FabricObject, Line } from 'fabric'
import { state } from './state'
import { installTextboxFit } from './widgets/textbox-fit'
import { CANVAS_PADDING, MM_TO_PX } from './constants'

type Flagged = FabricObject & { isGrid?: boolean; isBgImage?: boolean }

const CANVAS_EL_ID = 'labelCanvas'

// Fabric v7 changed the default text origin to center/center (v5 was left/top).
// The entire legacy editor positions text by its top-left corner, so restore the
// v5 default globally for all text classes (IText/Textbox inherit FabricText).
// Intentional center cases (e.g. the warning "!") set originX/Y explicitly.
FabricText.ownDefaults.originX = 'left'
FabricText.ownDefaults.originY = 'top'
// …and the same for EVERY object class: the barcode / QR / image inserters
// compute `left = w/2 - width/2` (top-left math), which with v7's center
// origin placed a new barcode half off the label's top-left edge — clipped,
// unscannable, and "printed on the total left side" in batch PDFs (customer
// report, 2026-08-25). Loaded designs carry their own explicit origin.
FabricObject.ownDefaults.originX = 'left'
FabricObject.ownDefaults.originY = 'top'
// Text boxes with a set height + vertical alignment (fit-to-box).
installTextboxFit()

/** Millimetre dimensions of the current label, honouring orientation. */
export function labelMm(): { w: number; h: number } {
  const { widthMM, heightMM } = state.currentSize
  return state.orientation === 'portrait'
    ? { w: heightMM, h: widthMM }
    : { w: widthMM, h: heightMM }
}

/** Pixel dimensions of the current label, honouring orientation. */
export function labelPx(): { w: number; h: number } {
  const { w, h } = labelMm()
  return { w: w * MM_TO_PX, h: h * MM_TO_PX }
}

/** Pasteboard around the label (scene px). Objects dragged past the label edge
 *  stay visible on it instead of vanishing under the label's edge (customer
 *  request); clicking it deselects. The label itself is painted by
 *  installLabelBackground; exports crop to the label (export.ts snapshotPng). */
export const PASTEBOARD_MM = 20 // minimum; grows to 40% of the label's longer side
const pad = (): number => { const { w, h } = labelPx(); return Math.max(PASTEBOARD_MM * MM_TO_PX, 0.4 * Math.max(w, h)) }

/** Trace the label outline (rect / rounded rect / ellipse) in scene coords. */
export function labelShapePath(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const shape = state.currentSize.shape
  ctx.beginPath()
  if (shape === 'round' || shape === 'oval') {
    ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2)
  } else if (state.roundedCorners) {
    const r = Math.min(labelCornerRadiusPx(), w / 2, h / 2)
    ctx.moveTo(r, 0); ctx.lineTo(w - r, 0); ctx.arcTo(w, 0, w, r, r); ctx.lineTo(w, h - r); ctx.arcTo(w, h, w - r, h, r)
    ctx.lineTo(r, h); ctx.arcTo(0, h, 0, h - r, r); ctx.lineTo(0, r); ctx.arcTo(0, 0, r, 0, r)
  } else {
    ctx.rect(0, 0, w, h)
  }
  ctx.closePath()
}

/** Die-cut corner radius (legacy 3.175mm / 0.125") in scene px; 0 when off. */
export function labelCornerRadiusPx(): number {
  const shape = state.currentSize.shape
  if (shape === 'round' || shape === 'oval') return 0
  return state.roundedCorners ? 3.175 * MM_TO_PX : 0
}

/** Replace Fabric's background fill: the canvas element is transparent (the
 *  grey pasteboard is the container's CSS) and the label is painted as a white
 *  shape with a hairline edge. While `__exporting` (snapshotPng) the whole
 *  crop is plain white, as before. */
function installLabelBackground(canvas: Canvas): void {
  const c = canvas as unknown as { _renderBackground: (ctx: CanvasRenderingContext2D) => void; __exporting?: boolean; width: number; height: number; viewportTransform: number[] }
  c._renderBackground = function (ctx: CanvasRenderingContext2D) {
    ctx.save()
    if (this.__exporting) {
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, this.width * 4, this.height * 4) // covers retina/multiplier sizes
      ctx.restore()
      return
    }
    const v = this.viewportTransform
    ctx.transform(v[0], v[1], v[2], v[3], v[4], v[5])
    const { w, h } = labelPx()
    labelShapePath(ctx, w, h)
    ctx.fillStyle = '#ffffff'
    ctx.fill()
    ctx.lineWidth = 1 / (v[0] || 1)
    ctx.strokeStyle = 'rgba(15, 23, 42, 0.35)'
    ctx.stroke()
    ctx.restore()
  }
}

/** Size the element to label + pasteboard at `factor` and pan so the label
 *  sits at scene (0,0) → element (pad·z, pad·z). */
function applyViewport(canvas: Canvas, factor: number): void {
  const { w, h } = labelPx()
  const P = pad()
  canvas.setDimensions({ width: (w + 2 * P) * factor, height: (h + 2 * P) * factor })
  canvas.setViewportTransform([factor, 0, 0, factor, P * factor, P * factor])
}

/** Create the single Fabric v7 canvas and store it in state. Returns it. */
export function initCanvas(): Canvas {
  const el = document.getElementById(CANVAS_EL_ID) as HTMLCanvasElement | null
  if (!el) throw new Error(`#${CANVAS_EL_ID} canvas element not found`)

  const { w, h } = labelPx()
  const canvas = new Canvas(el, {
    width: w,
    height: h,
    backgroundColor: '#ffffff',
    preserveObjectStacking: true,
    selection: true,
  })

  state.canvas = canvas
  installLabelBackground(canvas)
  resizeCanvas()

  // Scroll-wheel zoom (centered). Wheel up = zoom in, down = zoom out; prevent the
  // page from scrolling. Matches the live editor's wheel-to-zoom behaviour.
  canvas.on('mouse:wheel', (opt) => {
    const e = opt.e as WheelEvent
    e.preventDefault()
    e.stopPropagation()
    const next = state.zoomFactor * (e.deltaY > 0 ? 0.92 : 1.08)
    setZoom(next)
  })

  // Snap-to-grid (legacy snapObjectToGrid on object:moving): snap the object's
  // bounding-box edges — not its center-origin left/top — to grid multiples.
  canvas.on('object:moving', (opt) => {
    if (!state.snapToGrid || !state.gridEnabled) return
    const obj = opt.target as Flagged | undefined
    if (!obj || obj.isGrid || obj.isBgImage) return
    const step = state.gridSize * MM_TO_PX
    if (step < 2) return
    obj.setCoords() // mid-drag the cached coords lag behind left/top
    const b = obj.getBoundingRect()
    obj.set({
      left: (obj.left ?? 0) + (Math.round(b.left / step) * step - b.left),
      top: (obj.top ?? 0) + (Math.round(b.top / step) * step - b.top),
    })
  })

  // Phase 1+: wire cross-domain handlers here (selection:created/updated/cleared,
  // object:modified -> saveState, text:changed -> autoFitText, etc.) by importing
  // handlers from their modules — see map §3.3 item 1.
  return canvas
}

/**
 * Redraw the grid overlay from state (legacy drawGrid/clearGrid). Lines are
 * scene-space (zoom-independent), flagged isGrid so serialization, layers,
 * stats and exports all exclude them. Loading a design wipes them (they're
 * plain canvas objects), so every load path calls this again.
 */
export function refreshGrid(): void {
  const canvas = state.canvas
  if (!canvas) return
  canvas.getObjects().filter((o) => (o as Flagged).isGrid).forEach((o) => canvas.remove(o))
  if (state.gridEnabled) {
    const { w, h } = labelPx()
    const step = state.gridSize * MM_TO_PX
    if (step >= 2) {
      const props = {
        stroke: '#cbd5e1', strokeWidth: 0.5, selectable: false, evented: false,
        excludeFromExport: true, opacity: 0.4, originX: 'left' as const, originY: 'top' as const,
      }
      for (let x = step; x < w; x += step) {
        const l = new Line([x, 0, x, h], props) as Flagged
        l.isGrid = true
        canvas.add(l)
      }
      for (let y = step; y < h; y += step) {
        const l = new Line([0, y, w, y], props) as Flagged
        l.isGrid = true
        canvas.add(l)
      }
    }
  }
  canvas.requestRenderAll()
}

/** Resize the backing canvas to the current label size (legacy resizeCanvas). */
export function resizeCanvas(): void {
  const canvas = state.canvas
  if (!canvas) return
  applyViewport(canvas, state.zoomFactor || 1)
  applyCanvasShape()
  refreshGrid()
  canvas.requestRenderAll()
}

/** The label's die-cut shape (round / oval / rounded corners) is painted by
 *  installLabelBackground on every render; this just triggers a repaint. Kept
 *  as the entry point the toggles/tests call. (Older builds clipped the canvas
 *  element with border-radius — impossible now that the element also holds the
 *  pasteboard.) */
export function applyCanvasShape(): void {
  state.canvas?.requestRenderAll()
}

/** Current label outline, for tests/status: shape + corner radius in scene px. */
export function labelShape(): { shape: string; rounded: boolean; radiusPx: number } {
  const shape = state.currentSize.shape ?? 'rect'
  const round = shape === 'round' || shape === 'oval'
  return { shape, rounded: !round && !!state.roundedCorners, radiusPx: labelCornerRadiusPx() }
}

export function setZoom(factor: number): void {
  const canvas = state.canvas
  if (!canvas) return
  factor = Math.max(0.1, Math.min(8, factor))
  state.zoomFactor = factor
  applyViewport(canvas, factor)
  canvas.requestRenderAll()
  // Notify the toolbar (and anyone else) so the zoom % display stays in sync.
  document.dispatchEvent(new CustomEvent('olm:zoom', { detail: factor }))
}

export function zoomIn(): void { setZoom(state.zoomFactor + 0.1) }
export function zoomOut(): void { setZoom(state.zoomFactor - 0.1) }

/** Fit the label to the visible container (legacy fitToView). */
export function fitToView(): void {
  const canvas = state.canvas
  const container = document.getElementById('canvasContainer')
  if (!canvas || !container) return
  const { w, h } = labelPx()
  const availW = container.clientWidth - CANVAS_PADDING * 2
  const availH = container.clientHeight - CANVAS_PADDING * 2
  const P = pad()
  const factor = Math.max(0.1, Math.min(availW / (w + 2 * P), availH / (h + 2 * P), 8))
  setZoom(factor)
}
